import { DeliveryInstructionDetail, UserAddress } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/core-ui";
import {
    INSTRUCTOR_TEXT_PREFIX,
    DIRECTIONS_TO_REACH,
    VOICE_DIRECTION_TOOLTIP_KEY,
} from "@cart-client/constants/CartDeliveryInstruction";
import { DIRECTION_TO_REACH_TYPES } from "@cart-client/interfaces/CartDeliveryInstruction";
import { getBridgeSdk } from "@swiggy-private/checkout-core";
import { Address, EditDirectionsToReachResponsePayload, STORAGE_TYPES } from "@swiggy-private/native-bridge";

export const prepareConstructInstructorText = (
    selectedInstructions: Record<string, DeliveryInstructionDetail>,
): string => {
    if (!isEmpty(selectedInstructions)) {
        let resultStr = INSTRUCTOR_TEXT_PREFIX;
        const selectedInstructionValues = Object.values(selectedInstructions);
        const textArray = selectedInstructionValues.map((item) => item.instructor_text);
        const textArrayLen = textArray.length;

        switch (textArrayLen) {
            case 0: {
                resultStr = "";
                break;
            }
            case 1: {
                resultStr += textArray[0];
                break;
            }
            default: {
                resultStr += `${textArray.slice(0, textArrayLen - 1).join(", ")} & ${textArray[textArrayLen - 1]}`;
            }
        }

        // Add conditional text at the end for COD, only applicable for leave at door/security
        if (resultStr) {
            const filteredArr = selectedInstructionValues.filter(
                (el: DeliveryInstructionDetail) => el.conditional_text != "",
            );
            if (filteredArr.length) {
                resultStr += ` (${filteredArr[0].conditional_text})`;
            }
        }

        return resultStr;
    } else {
        return "";
    }
};

export const prepareRequestPayload = (instructions: Record<string, DeliveryInstructionDetail>): string[] => {
    // helper function which will send all the selected drop off instructions except directions to reach
    if (!isEmpty(instructions)) {
        const selectedInstructionValues = Object.values(instructions);
        const filteredIds = selectedInstructionValues
            .filter((item: DeliveryInstructionDetail) => item.instruction_type !== DIRECTIONS_TO_REACH)
            .map((item: DeliveryInstructionDetail) => {
                return item.id;
            });
        return filteredIds;
    }
    return [];
};

// method to check if cart has selected address or not
export const checkForAddressValidity = (selectedAddress: UserAddress | undefined): boolean => {
    let validAddress = false;
    if ((selectedAddress && !selectedAddress.delivery_valid) || !selectedAddress) {
        validAddress = false;
    } else {
        validAddress = true;
    }

    return validAddress;
};

export async function openEditDirectionsToReach(
    updatedAddress: UserAddress,
): Promise<EditDirectionsToReachResponsePayload | undefined> {
    return getBridgeSdk().editDirectionsToReach({
        address: {
            ...updatedAddress,
            annotationTag: updatedAddress.addressCategory,
            userIntentAnnotationTag: updatedAddress?.userIntentAddressCategory,
        } as Address,
    });
}

/**
 * This function returns the index of the 'DIRECTIONS_TO_REACH' instruction type in the array of
 * instructions
 */
export const getDirectionsToReachCardPosition = (instructions: DeliveryInstructionDetail[]): number => {
    if (!isEmpty(instructions)) {
        return instructions.findIndex(
            (item: DeliveryInstructionDetail) => item.instruction_type === DIRECTIONS_TO_REACH,
        );
    }
    return -1;
};

/**
 * If the selected address has a voice directions added, return true, otherwise return false.
 */
export const isVoiceDirectionsAdded = (address?: UserAddress): boolean => !isEmpty(address?.voice_directions_s3_uri);

/**
 * This function returns a string that represents the type of directions that are available for the selected address
 */
export const getDirectionsToReachStatus = (selectedAddress?: UserAddress): DIRECTION_TO_REACH_TYPES => {
    const isTextDirectionsPresent = !isEmpty(selectedAddress?.instructions);
    const isVoiceDirectionsPresent = isVoiceDirectionsAdded(selectedAddress);

    if (isTextDirectionsPresent && isVoiceDirectionsPresent) {
        return DIRECTION_TO_REACH_TYPES.VOICE_AND_TEXT;
    } else if (isTextDirectionsPresent) {
        return DIRECTION_TO_REACH_TYPES.TEXT_ONLY;
    } else if (isVoiceDirectionsPresent) {
        return DIRECTION_TO_REACH_TYPES.VOICE_ONLY;
    }
    return DIRECTION_TO_REACH_TYPES.NOT_ADDED;
};

/**
 * This function gets the current count of the voice direction tooltip from app storage
 */
export const getVoiceDirectionTooltipCount = async (): Promise<number | undefined> => {
    try {
        const response = await getBridgeSdk().storage({
            type: STORAGE_TYPES.GET,
            data: {
                [VOICE_DIRECTION_TOOLTIP_KEY]: "",
            },
        });
        const currentCount = response?.data[VOICE_DIRECTION_TOOLTIP_KEY];
        if (!currentCount) {
            return Promise.resolve(0);
        }
        return Promise.resolve(JSON.parse(currentCount));
    } catch (_) {
        return;
    }
};

/**
 * This function updates the count of the voice direction tooltip in app storage
 */
export const updateVoiceDirectionTooltipCount = async (updatedCount: number): Promise<void> => {
    getBridgeSdk().storage({
        type: STORAGE_TYPES.SAVE,
        data: {
            [VOICE_DIRECTION_TOOLTIP_KEY]: JSON.stringify(updatedCount),
        },
    });
};

/**
 * This function returns true if the user's current address does not have a voice instruction already added
 */
export const canShowVoiceDirectionTooltip = (updatedAddress: UserAddress): boolean =>
    !isVoiceDirectionsAdded(updatedAddress);

/**
 * This function returns a JSON string that contains the context needed for ARD events
 */
export const getVoiceDirectionContext = ({
    selectedAddress,
    position,
    cartId,
    addressId,
    title,
    displayText,
}: {
    cartId: string;
    addressId: string;
    selectedAddress?: UserAddress;
    title?: string;
    position?: number;
    displayText?: string;
}): string => {
    return JSON.stringify({
        position,
        cartId,
        addressId,
        ...(selectedAddress && { instructionType: getDirectionsToReachStatus(selectedAddress) }),
        ...(position && { position }),
        ...(title && { title }),
        ...(displayText && { displayText }),
    });
};
